<template>
  <div class="flex mb-4 gap-4 items-center">
    <!-- Card with item details and quantity -->
    <div
      class="flex justify-between items-center py-2 px-3 rounded-lg border-2 transition-colors flex-1"
      :class="{
        'border-action bg-white': quantity > 0,
        'border-gray-200 bg-white': quantity === 0,
        'bg-blue-50': isExtraCard && quantity > 0
      }"
    >
      <!-- Item details -->
      <div class="flex-1">
        <div class="text-normal-emphasis font-medium">{{ item.name }}</div>
        <div class="text-normal-reduced">{{ formatCurrency(item.amount) }}</div>
      </div>

      <!-- Controls and quantity -->
      <div v-if="!disableAdd" class="flex items-center gap-4">
        <!-- Add/Remove Controls -->
        <div v-if="quantity === 0" class="cursor-pointer text-action text-base" @click="setQuantity(1)">
          <i class="fas fa-plus-circle"></i>
        </div>
        <div v-else class="cursor-pointer text-action text-base" @click="setQuantity(0)">
          <i class="fas fa-trash"></i>
        </div>

        <!-- Quantity Selector (only shown when quantity > 0) -->
        <div v-if="quantity > 0" class="w-16">
          <AppSelect
            v-model="quantity"
            :disable-sorting="true"
            :options="quantityOptions"
            :name="name"
            class="w-full"
          />
        </div>
      </div>
    </div>

    <!-- Price outside the border -->
    <div class="w-[60px] text-right text-normal-emphasis font-medium">
      {{ formatCurrency(totalPrice, false) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AppSelect from '@/components/AppSelect.vue';
import { SelectOption } from '@/models/SelectOption';
import useLocalization from '@/modules/useLocalization';
import { Item } from '@models/Item';

const { formatCurrency } = useLocalization();

interface Props {
  item: Item;
  modelValue: number;
  quantityOptions: SelectOption[];
  name?: string;
  isExtraCard?: boolean;
  disableAdd?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  name: '',
  isExtraCard: false
});

const emit = defineEmits(['update:modelValue']);

const quantity = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', typeof value === 'string' ? Number(value) : value)
});

const totalPrice = computed(() => {
  return (props.item.amount * quantity.value);
});

// Method to set the quantity directly
const setQuantity = (value: number) => {
  emit('update:modelValue', value);
};
</script>
